import React from 'react';
import './ScreeningTypeSelector.scss';
import {
  InternalApiCustomList,
  InternalApiCustomListScreeningField,
  InternalApiScreeningMatchType,
} from '../../api-internal';
import { OptionType } from '../../form/FormField';
import { SalvCheckbox, SalvDropdown, SalvNumberField, SalvSwitch, SalvCard, Size } from '@salvapp/ui-components';
import useCustomLists from '../../custom-lists/useCustomLists';
import { CustomListType } from '../../custom-lists/customListsReducer';
import { useQueryCustomListScreeningUsableFields } from '../../custom-lists/usableFields/customListScreeningUsableFieldsActions';
import { DowJonesCustomListCode } from '../configure/ScreeningDataSetup';
import { showFeedback } from '../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';
import { ApiCustomListWithFields, ApiCustomListWithFieldsScreeningFieldIdsInner } from './SearchPanel';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';

export type ListSearchInput = 'SCORE' | 'PREFIX_LENGTH';

interface Props {
  selectedListFields: ApiCustomListWithFields[];
  onListToggle: (listId: string) => void;
  onPathToggle: (listId: string, field: InternalApiCustomListScreeningField) => void;
  onPathTypeChange: (listId: string, pathId: string, matchType: InternalApiScreeningMatchType) => void;
  onInputChange: (listId: string, pathId: string, value: number, type: ListSearchInput) => void;
}

const CustomListSearchSelector = (props: Props) => {
  const { selectedListFields, onListToggle, onPathToggle, onPathTypeChange, onInputChange } = props;
  const { features } = useMe();
  const isDjEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DJ_SCREENING, features);

  const lists = useCustomLists().filter(
    (list) => !Object.values(DowJonesCustomListCode).includes(list.code as DowJonesCustomListCode)
  );

  if (!lists || lists.length === 0) {
    return null;
  }
  return (
    <div id='custom-list-search-accordion' data-testid='custom-list-search-accordion'>
      <SalvCard expansionOptions={{ isDefaultExpanded: !isDjEnabled }}>
        <SalvCard.Header>Custom Lists</SalvCard.Header>
        <SalvCard.Body>
          {lists.map((list, i) => (
            <ListInfo
              key={i}
              list={list}
              selectedListFields={selectedListFields}
              onListToggle={onListToggle}
              onPathToggle={onPathToggle}
              onInputChange={onInputChange}
              onPathTypeChange={onPathTypeChange}
            />
          ))}
        </SalvCard.Body>
      </SalvCard>
    </div>
  );
};

interface ListInfoProps extends Pick<
  Props,
  'selectedListFields' | 'onListToggle' | 'onPathToggle' | 'onPathTypeChange' | 'onInputChange'
> {
  list: CustomListType;
}

const ListInfo = ({
  list,
  selectedListFields,
  onListToggle,
  onPathToggle,
  onInputChange,
  onPathTypeChange,
}: ListInfoProps) => {
  const dispatch = useDispatch();
  const { data: screeningFields, isPending, isError } = useQueryCustomListScreeningUsableFields(list.id!);
  if (isError) {
    dispatch(
      showFeedback({
        message: 'Could not load Custom List Screening fields',
        success: false,
      })
    );
    return <div />;
  }

  const listIsSelected = (listId: string) => {
    return selectedListFields.map((path) => path.id).includes(listId);
  };

  const fieldIsSelected = (fieldId: string) => {
    const selectedScreeningFieldIds = selectedListFields.flatMap((lp) => lp.screeningFieldIds).map((p) => p.id);
    return selectedScreeningFieldIds.includes(fieldId);
  };

  const getCurrentScreeningFieldId = (
    list: InternalApiCustomList,
    field: InternalApiCustomListScreeningField
  ): ApiCustomListWithFieldsScreeningFieldIdsInner => {
    return (
      selectedListFields?.find((it) => it.id === list.id)?.screeningFieldIds.find((it) => it.id === field.id) ||
      ({} as ApiCustomListWithFieldsScreeningFieldIdsInner)
    );
  };

  const selected = listIsSelected(list.id!);

  return (
    !isPending && (
      <div className={'m-b-8'}>
        <SalvSwitch
          label={list.name}
          selected={selected}
          onChange={() => {
            onListToggle(list.id!);
          }}
          disabled={(screeningFields?.length ?? 0) === 0}
        />
        {selected &&
          screeningFields?.map((field) => (
            <div key={field.id}>
              <SalvCheckbox
                label={field.name}
                selected={fieldIsSelected(field.id!)}
                onChange={() => onPathToggle(list.id!, field)}
              />
              {fieldIsSelected(field.id!) && (
                <MatchConfiguration
                  field={field}
                  list={list}
                  onPathTypeChange={onPathTypeChange}
                  onInputChange={onInputChange}
                  getCurrentScreeningFieldId={getCurrentScreeningFieldId}
                />
              )}
            </div>
          ))}
      </div>
    )
  );
};

interface MatchConfigurationProps extends Pick<Props, 'onPathTypeChange' | 'onInputChange'> {
  field: InternalApiCustomListScreeningField;
  list: InternalApiCustomList;
  getCurrentScreeningFieldId: (
    list: InternalApiCustomList,
    field: InternalApiCustomListScreeningField
  ) => ApiCustomListWithFieldsScreeningFieldIdsInner;
}

const MatchConfiguration = ({
  field,
  list,
  onPathTypeChange,
  onInputChange,
  getCurrentScreeningFieldId,
}: MatchConfigurationProps) => {
  const screeningFieldId = getCurrentScreeningFieldId(list, field);
  const { screeningSubType, score, prefixMatchLimit } = screeningFieldId;

  const getScreeningStrategy = (matchType: InternalApiScreeningMatchType) => {
    switch (matchType) {
      case InternalApiScreeningMatchType.FUZZY_MATCH:
        return 'Fuzzy match';
      case InternalApiScreeningMatchType.STARTS_WITH:
        return 'Starts with';
      case InternalApiScreeningMatchType.EXACT_MATCH:
        return 'Exact match';
      case InternalApiScreeningMatchType.CONTAINS:
        return 'Contains';
      case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        return 'Fuzzy contains';
    }
  };

  const createOptionType = (matchType?: InternalApiScreeningMatchType) => {
    if (!matchType) {
      return null;
    }
    return toOptionType(matchType);
  };

  const toOptionType = (matchType: InternalApiScreeningMatchType): OptionType => {
    return {
      key: matchType,
      label: getScreeningStrategy(matchType),
      value: matchType,
    };
  };

  return (
    <div className='mb-3'>
      <SalvDropdown
        ariaLabel={'Screening subtype'}
        options={Object.values(InternalApiScreeningMatchType).map(toOptionType)}
        value={createOptionType(screeningSubType) ?? undefined}
        clearable={false}
        onChange={([option]) => {
          onPathTypeChange(list.id!, field.id!, option.value as InternalApiScreeningMatchType);
        }}
        size={Size.SMALL}
      />
      {screeningSubType === InternalApiScreeningMatchType.FUZZY_MATCH && (
        <>
          <SalvNumberField
            className='mt-3'
            label='Score'
            value={score ?? 95}
            onChange={(value) => onInputChange(list.id!, field.id!, value, 'SCORE')}
            minValue={90}
            maxValue={100}
            size={Size.SMALL}
          />
        </>
      )}
      {screeningSubType === InternalApiScreeningMatchType.STARTS_WITH && (
        <SalvNumberField
          className='mt-3'
          label='Prefix'
          value={prefixMatchLimit ?? 8}
          onChange={(value) => onInputChange(list.id!, field.id!, value, 'PREFIX_LENGTH')}
          minValue={1}
          maxValue={8}
          size={Size.SMALL}
        />
      )}
    </div>
  );
};

export default CustomListSearchSelector;
